// App. Two modes:
//   - Demo:  cycle button steps through DEMO_STORYBOARDS[0]; for visual review.
//   - Embed: hosted inside voice_agent.html; subscribes to window CustomEvents
//            dispatched by the live WebSocket handler. No demo, no DemoControls.

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePresentationStore } from './store/presentationStore';
import { SceneRunner } from './stage/SceneRunner';
import { Orb } from './stage/Orb';
import { useAudioCueSync } from './stage/useAudioCueSync';
import { DEMO_STORYBOARDS } from './data/demo-storyboard';
import type { Storyboard } from './types';

interface AppProps {
  embedMode?: boolean;
}

export default function App({ embedMode = false }: AppProps) {
  const storyboard = usePresentationStore((s) => s.storyboard);
  const currentSceneIndex = usePresentationStore((s) => s.currentSceneIndex);
  const ingest = usePresentationStore((s) => s.ingest);
  const advance = usePresentationStore((s) => s.advance);
  const interrupt = usePresentationStore((s) => s.interrupt);

  // Demo mode: auto-load on mount.
  useEffect(() => {
    if (!embedMode) ingest(DEMO_STORYBOARDS[0]);
  }, [embedMode, ingest]);

  // Embed mode: rAF-driven scene advancement, anchored to real audio start.
  useAudioCueSync(embedMode);

  // Embed mode: subscribe to live events from voice_agent.html.
  useEffect(() => {
    if (!embedMode) return;
    const onStoryboard = (e: Event) => {
      const sb = (e as CustomEvent<{ storyboard: Storyboard }>).detail?.storyboard;
      if (sb && Array.isArray(sb.scenes) && sb.scenes.length) ingest(sb);
    };
    const onInterrupt = () => interrupt();
    const onAdvance = () => advance();
    window.addEventListener('concierge:storyboard', onStoryboard);
    window.addEventListener('concierge:interrupt',  onInterrupt);
    window.addEventListener('concierge:advance',    onAdvance);
    return () => {
      window.removeEventListener('concierge:storyboard', onStoryboard);
      window.removeEventListener('concierge:interrupt',  onInterrupt);
      window.removeEventListener('concierge:advance',    onAdvance);
    };
  }, [embedMode, ingest, interrupt, advance]);

  const currentScene =
    storyboard && currentSceneIndex >= 0 ? storyboard.scenes[currentSceneIndex] ?? null : null;

  // Idle screen — just the orb in the center if no storyboard active.
  if (!currentScene) {
    return (
      <div className="relative w-screen h-[100dvh] overflow-hidden bg-void-radial flex items-center justify-center">
        <Orb size={140} />
        {!embedMode && (
          <DemoControls
            onLoad={() => ingest(DEMO_STORYBOARDS[0])}
            onAdvance={advance}
            onInterrupt={interrupt}
            atIndex={currentSceneIndex}
            totalScenes={storyboard?.scenes.length ?? 0}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <SceneRunner scene={currentScene} />
      {!embedMode && (
        <DemoControls
          onLoad={() => ingest(DEMO_STORYBOARDS[0])}
          onAdvance={advance}
          onInterrupt={interrupt}
          atIndex={currentSceneIndex}
          totalScenes={storyboard?.scenes.length ?? 0}
        />
      )}
    </>
  );
}

interface ControlsProps {
  onLoad:      () => void;
  onAdvance:   () => void;
  onInterrupt: () => void;
  atIndex:     number;
  totalScenes: number;
}

function DemoControls({ onLoad, onAdvance, onInterrupt, atIndex, totalScenes }: ControlsProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.85 }}
      transition={{ delay: 1, duration: 0.6 }}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-void-800/80 border border-fg-faint/40 backdrop-blur-md shadow-card"
    >
      <button
        onClick={onLoad}
        className="text-eyebrow text-fg-mute uppercase font-medium px-3 py-1.5 rounded-full hover:text-fg hover:bg-fg/5 transition-colors"
      >
        Restart
      </button>
      <span className="text-eyebrow text-fg-faint">·</span>
      <button
        onClick={onAdvance}
        disabled={atIndex >= totalScenes - 1}
        className="text-eyebrow text-fg uppercase font-medium px-3 py-1.5 rounded-full hover:bg-fg/5 transition-colors disabled:text-fg-faint disabled:cursor-not-allowed"
      >
        {atIndex < totalScenes - 1 ? 'Next →' : 'End'}
      </button>
      <span className="text-eyebrow text-fg-faint">·</span>
      <button
        onClick={onInterrupt}
        className="text-eyebrow text-fg-mute uppercase font-medium px-3 py-1.5 rounded-full hover:text-fg hover:bg-fg/5 transition-colors"
      >
        Stop
      </button>
      <span className="text-eyebrow font-mono text-fg-faint ml-2">
        {atIndex + 1} / {totalScenes}
      </span>
    </motion.div>
  );
}
